import { ActivityMonitor } from "app/common/activity_monitor";
import { AjaxClient } from "app/common/ajax";
import { readCookie, writeCookie } from "app/common/cookies";
import { IDisposable } from "app/common/disposable";
import { noun } from "app/common/text";
import { ITimeSource } from "app/common/time_source";
import { IUpdateFunction, UpdateLoop } from "app/common/update_loop";
import * as $ from "jquery";
import * as moment from "moment";
import { INotification, NotificationManager } from "./notifications";
import { initializeClock } from "./server_clock";

type ClockSetting = "hide" | "compact" | "show";

const allClockSettings: ClockSetting[] = [ "hide", "compact", "show" ];
const defaultClockSetting: ClockSetting = "show";

const clockCookieKey = "nav-clock-shown";

export function initializeNavBar(
    activityMonitor: ActivityMonitor,
    ajaxClient: AjaxClient,
    notifications: NotificationManager,
    timeSource: ITimeSource,
): UpdateLoop {
    const navBarUpdateFn = new NavBarUpdateFunction(ajaxClient, notifications);
    const updater = new UpdateLoop(activityMonitor, navBarUpdateFn, moment.duration(20, "s"));
    updater.enableUpdateLoop();

    initializeNotifications(notifications);
    initializeNavClock(timeSource);
    attachDomEvents(activityMonitor, notifications, updater);

    return updater;
}

function initializeNotifications(notifications: NotificationManager): void {
    const notificationsElmt = $("#notifications");

    notifications.synchroniseWithHtmlElement(notificationsElmt);

    const notificationsMuted = notifications.areNotificationsMuted;
    updateNotificationIcon(notificationsMuted);
    if (notificationsMuted) {
        notificationsElmt.hide();
    }

    notifications.notificationAdded.subscribe(() => {
        updateNotificationCount(notifications.shownNotificationCount, notifications.totalNotificationCount);
    });

    notifications.notificationRemoved.subscribe(() => {
        updateNotificationCount(notifications.shownNotificationCount, notifications.totalNotificationCount);
    });
}

export class NavBarUpdateFunction implements IUpdateFunction {
    public constructor(
        private readonly ajaxClient: AjaxClient,
        private readonly notifications: NotificationManager,
    ) {}

    public async update(): Promise<void> {
        const result = await this.ajaxClient.call<INavBarAjaxResult>("/navbar");

        $("#num-messages").text(result.numMessages.toString());
        this.setLinkYellowness($("#num-messages"), result.numMessages > 0);

        $("#num-turns").text(result.numTurns.toString());
        this.setLinkYellowness($("#num-turns"), result.numTurns > 0);

        $("#num-pending-live").text(result.numPendingLiveGames.toString());
        $("#num-pending-ltg").text(result.numPendingLongTermGames.toString());
        $("#notifications-link .count").text(result.numNotifications.toString());

        this.notifications.setNotificationsFromRemote(
            Array.from(result.notifications.values()), result.numNotifications);
    }

    private setLinkYellowness(elmt: JQuery<HTMLElement>, yellow: boolean) {
        if (yellow) {
            elmt.parent().addClass("yellow-link");
        } else {
            elmt.parent().removeClass("yellow-link");
        }
    }
}

interface INavBarAjaxResult {
    numMessages: number;
    numTurns: number;
    numPendingLiveGames: number;
    numPendingLongTermGames: number;
    numNotifications: number;
    notifications: Map<number, INotification>;
}

function attachDomEvents(
    activityMonitor: ActivityMonitor,
    notifications: NotificationManager,
    updater: UpdateLoop,
): void {
    $("#navbar-refresh").on("click", () => {
        $("#num-messages").text("?");
        $("#num-turns").text("?");
        void updater.updateImmediately();
        return false;
    });

    // Notifications get hidden/removed using the show-hide-link mechanic.
    // We attach an event that gets called after that to make sure our state matches that.
    // The setTimeout makes sure our event gets deleted, allowing the show-hide-link event to be finished first.
    $("#notifications-link-mute").on("click", () => setTimeout(() => {
        if ($("#notifications").is(":visible")) {
            notifications.unmuteNotifications();
            updateNotificationIcon(/* notificationsMuted= */ false);
        } else {
            notifications.muteNotifications();
            updateNotificationIcon(/* notificationsMuted= */ true);
        }
    }, 0));

    $("#navbar-debug-refresh-controller").on("click", () => {
        const elmt = $("#navbar-debug-refresh-controller");
        if (elmt.html() === "Stop auto refresh") {
            activityMonitor.pauseForDebugging();
            elmt.text("Start auto refresh");
        } else {
            activityMonitor.resumeForDebugging();
            elmt.text("Stop auto refresh");
        }
        return false;
    });
}

function updateNotificationIcon(notificationsMuted: boolean): void {
    const icon = $("#notifications-link i");
    icon.removeClass("fa-bell-o fa-bell-slash-o");
    icon.addClass(notificationsMuted ? "fa-bell-slash-o" : "fa-bell-o");
}

function updateNotificationCount(shownNotificationCount: number, totalNotificationCount: number): void {
    const icon = $("#notifications-link .count");
    const muter = $("#notifications-link-mute");

    $("#notifications-link .tt-inner")
        .text(`${shownNotificationCount}/${totalNotificationCount} `
            + `${noun("notification", "notifications", totalNotificationCount)}`);

    if (totalNotificationCount === 0) {
        icon.hide();
        muter.toggle(!$("#notifications").is(":visible"));
    } else {
        icon.show();
        muter.show();
    }
}

function initializeNavClock(timeSource: ITimeSource) {
    const elmt = $("#nav-clock");
    let disposableClock = createClock(elmt, timeSource);

    $("#nav-clock-toggle").on("click", () => {
        advanceClockSetting();
        disposableClock.dispose();
        disposableClock = createClock(elmt, timeSource);
        return false;
    });
}

function getClockSetting(): ClockSetting {
    return readCookie(clockCookieKey) as ClockSetting ?? defaultClockSetting;
}

function advanceClockSetting(): void {
    const currSetting = getClockSetting();
    let currIndex = allClockSettings.indexOf(currSetting);
    currIndex = (currIndex + 1) % allClockSettings.length;
    const newSetting = allClockSettings[currIndex];

    writeCookie(clockCookieKey, newSetting);
}

function createClock(elmt: JQuery<HTMLElement>, timeSource: ITimeSource): IDisposable {
    const setting = getClockSetting();
    if (setting === "hide") {
        elmt.text("");
        elmt.hide();
        return { dispose: () => { /* do nothing */ } };
    } else {
        elmt.show();
        const seconds = getClockSetting() === "compact" ? "" : ":ss";
        const format = elmt.data("format") === "24h" ? `HH:mm${seconds}` : `hh:mm${seconds} a`;
        return initializeClock(elmt, timeSource, format);
    }
}
